import React, { useEffect, useState, useCallback } from 'react';
import { Toolbar, Item as ToolbarItem } from 'devextreme-react/toolbar';
import Button from 'devextreme-react/button';
import TextBox from 'devextreme-react/text-box';
import SelectBox from 'devextreme-react/select-box';
import ScrollView from 'devextreme-react/scroll-view';
import notify from 'devextreme/ui/notify';
import { confirm } from 'devextreme/ui/dialog';
import { useSearchParams } from 'react-router-dom';

import {
  CompanySettings,
  getCompanySettings,
  updateCompanySettings,
  configureAccountingProvider,
  removeAccountingProvider,
  initiateExactOnlineOAuth,
} from '../../api/company-settings';
import { useAuth } from '../../contexts/auth';
import './company-settings.scss';

const providerOptions = [
  { value: 'None', text: 'Geen' },
  { value: 'ExactOnline', text: 'Exact Online' },
];

export const CompanySettingsPage = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Company info
  const [companyName, setCompanyName] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  // Company address
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyAddressNr, setCompanyAddressNr] = useState('');
  const [companyZipCode, setCompanyZipCode] = useState('');
  const [companyCity, setCompanyCity] = useState('');

  // Email settings
  const [emailFromAddress, setEmailFromAddress] = useState('');
  const [emailFromName, setEmailFromName] = useState('');

  // Accounting provider
  const [providerType, setProviderType] = useState('None');
  const [exactClientId, setExactClientId] = useState('');
  const [exactClientSecret, setExactClientSecret] = useState('');
  const [exactCallbackUrl, setExactCallbackUrl] = useState('');
  const [hasExactSecret, setHasExactSecret] = useState(false);
  const [hasExactConnection, setHasExactConnection] = useState(false);

  const loadSettings = useCallback(async() => {
    setIsLoading(true);
    try {
      const data: CompanySettings = await getCompanySettings(user?.token);
      setCompanyName(data.CompanyName || '');
      setContactEmail(data.ContactEmail || '');
      setCompanyAddress(data.CompanyAddress || '');
      setCompanyAddressNr(data.CompanyAddressNr || '');
      setCompanyZipCode(data.CompanyZipCode || '');
      setCompanyCity(data.CompanyCity || '');
      setEmailFromAddress(data.EmailFromAddress || '');
      setEmailFromName(data.EmailFromName || '');
      setProviderType(data.AccountingProviderType || 'None');
      setExactClientId(data.ExactOnlineClientId || '');
      setExactCallbackUrl(data.ExactOnlineCallbackUrl || '');
      setExactClientSecret(''); // never returned from server
      setHasExactSecret(data.HasExactOnlineSecret);
      setHasExactConnection(data.HasExactOnlineConnection);
    } catch {
      notify('Fout bij laden van instellingen.', 'error', 3000);
    } finally {
      setIsLoading(false);
    }
  }, [user?.token]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Handle OAuth redirect result (?exact=connected or ?exact=error)
  useEffect(() => {
    const exactResult = searchParams.get('exact');
    if (exactResult === 'connected') {
      notify('Exact Online is succesvol verbonden!', 'success', 4000);
      searchParams.delete('exact');
      setSearchParams(searchParams, { replace: true });
      loadSettings(); // refresh to show updated connection status
    } else if (exactResult === 'error') {
      notify('Fout bij verbinden met Exact Online. Probeer opnieuw.', 'error', 4000);
      searchParams.delete('exact');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, loadSettings]);

  const saveCompanyInfo = useCallback(async() => {
    setIsSaving(true);
    try {
      await updateCompanySettings(
        {
          CompanyName: companyName,
          ContactEmail: contactEmail,
          CompanyAddress: companyAddress,
          CompanyAddressNr: companyAddressNr,
          CompanyZipCode: companyZipCode,
          CompanyCity: companyCity,
          EmailFromAddress: emailFromAddress,
          EmailFromName: emailFromName,
        },
        user?.token
      );
      notify('Instellingen opgeslagen.', 'success', 3000);
    } catch {
      notify('Fout bij opslaan van instellingen.', 'error', 3000);
    } finally {
      setIsSaving(false);
    }
  }, [companyName, contactEmail, companyAddress, companyAddressNr, companyZipCode, companyCity, emailFromAddress, emailFromName, user?.token]);

  const saveAccountingProvider = useCallback(async() => {
    setIsSaving(true);
    try {
      const result = await configureAccountingProvider(
        {
          ProviderType: providerType,
          ExactOnlineClientId: exactClientId,
          ExactOnlineClientSecret: exactClientSecret,
          ExactOnlineCallbackUrl: exactCallbackUrl || undefined,
        },
        user?.token
      );
      notify(result.Message || 'Boekhoudkoppeling opgeslagen.', 'success', 3000);
      if (result.NextStep) {
        notify(result.NextStep, 'info', 6000);
      }
      await loadSettings();
    } catch {
      notify('Fout bij opslaan van boekhoudkoppeling.', 'error', 3000);
    } finally {
      setIsSaving(false);
    }
  }, [providerType, exactClientId, exactClientSecret, exactCallbackUrl, user?.token, loadSettings]);

  const handleRemoveProvider = useCallback(async() => {
    const confirmed = await confirm(
      'Weet u zeker dat u de boekhoudkoppeling wilt verwijderen? Dit verwijdert alle opgeslagen credentials.',
      'Koppeling verwijderen'
    );
    if (!confirmed) return;

    setIsSaving(true);
    try {
      await removeAccountingProvider(user?.token);
      notify('Boekhoudkoppeling verwijderd.', 'success', 3000);
      setProviderType('None');
      setExactClientId('');
      setExactClientSecret('');
      setExactCallbackUrl('');
      setHasExactConnection(false);
    } catch {
      notify('Fout bij verwijderen van koppeling.', 'error', 3000);
    } finally {
      setIsSaving(false);
    }
  }, [user?.token]);

  const handleConnectExact = useCallback(async() => {
    try {
      setIsSaving(true);
      const result = await initiateExactOnlineOAuth(user?.token);
      if (result.LoginUrl) {
        // Redirect browser to Exact Online login page
        window.location.href = result.LoginUrl;
      } else {
        // Already connected
        notify(result.Message || 'Exact Online is al verbonden.', 'success', 3000);
        await loadSettings();
      }
    } catch (err: any) {
      const msg = err?.response?.data || 'Fout bij starten van Exact Online koppeling.';
      notify(typeof msg === 'string' ? msg : 'Fout bij starten van Exact Online koppeling.', 'error', 4000);
    } finally {
      setIsSaving(false);
    }
  }, [user?.token, loadSettings]);

  return (
    <ScrollView className='view-wrapper-scroll'>
      <div className='view-wrapper view-wrapper-company-settings'>
        <Toolbar className='toolbar-details'>
          <ToolbarItem location='before'>
            <span className='toolbar-title'>Bedrijfsinstellingen</span>
          </ToolbarItem>
          <ToolbarItem location='after'>
            <Button
              text='Vernieuwen'
              icon='refresh'
              stylingMode='text'
              onClick={loadSettings}
            />
          </ToolbarItem>
        </Toolbar>

        {isLoading ? (
          <div className='loading-placeholder'>Laden...</div>
        ) : (
          <div className='panels'>
            {/* ── Bedrijfsgegevens ── */}
            <div className='dx-card info-card'>
              <div className='card-header'>
                <i className='dx-icon dx-icon-home card-header-icon' />
                <span>Bedrijfsgegevens</span>
              </div>
              <div className='card-body-form'>
                <div className='form-row'>
                  <label className='form-label'>Bedrijfsnaam</label>
                  <TextBox
                    value={companyName}
                    onValueChanged={(e) => setCompanyName(e.value)}
                    placeholder='Naam van het bedrijf'
                    stylingMode='outlined'
                  />
                </div>
                <div className='form-row'>
                  <label className='form-label'>Contact E-mail</label>
                  <TextBox
                    value={contactEmail}
                    onValueChanged={(e) => setContactEmail(e.value)}
                    placeholder='info@bedrijf.nl'
                    stylingMode='outlined'
                  />
                </div>
              </div>
            </div>

            {/* ── Bedrijfsadres ── */}
            <div className='dx-card info-card'>
              <div className='card-header'>
                <i className='dx-icon dx-icon-map card-header-icon' />
                <span>Bedrijfsadres</span>
              </div>
              <div className='card-body-form'>
                <div className='form-row hint-row'>
                  <span className='hint-text'>
                    Dit adres wordt gebruikt om de afstand (km) naar de werklocatie automatisch te berekenen.
                  </span>
                </div>
                <div className='form-row form-row-inline'>
                  <div className='form-field flex-3'>
                    <label className='form-label'>Straat</label>
                    <TextBox
                      value={companyAddress}
                      onValueChanged={(e) => setCompanyAddress(e.value)}
                      placeholder='Straatnaam'
                      stylingMode='outlined'
                    />
                  </div>
                  <div className='form-field flex-1'>
                    <label className='form-label'>Nr</label>
                    <TextBox
                      value={companyAddressNr}
                      onValueChanged={(e) => setCompanyAddressNr(e.value)}
                      placeholder='Nr'
                      stylingMode='outlined'
                    />
                  </div>
                </div>
                <div className='form-row form-row-inline'>
                  <div className='form-field flex-1'>
                    <label className='form-label'>Postcode</label>
                    <TextBox
                      value={companyZipCode}
                      onValueChanged={(e) => setCompanyZipCode(e.value)}
                      placeholder='1234 AB'
                      stylingMode='outlined'
                    />
                  </div>
                  <div className='form-field flex-3'>
                    <label className='form-label'>Plaats</label>
                    <TextBox
                      value={companyCity}
                      onValueChanged={(e) => setCompanyCity(e.value)}
                      placeholder='Plaatsnaam'
                      stylingMode='outlined'
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* ── E-mail Instellingen ── */}
            <div className='dx-card info-card'>
              <div className='card-header'>
                <i className='dx-icon dx-icon-email card-header-icon' />
                <span>E-mail Instellingen</span>
              </div>
              <div className='card-body-form'>
                <div className='form-row'>
                  <label className='form-label'>Afzender E-mail</label>
                  <TextBox
                    value={emailFromAddress}
                    onValueChanged={(e) => setEmailFromAddress(e.value)}
                    placeholder='noreply@bedrijf.nl'
                    stylingMode='outlined'
                  />
                </div>
                <div className='form-row'>
                  <label className='form-label'>Afzender Naam</label>
                  <TextBox
                    value={emailFromName}
                    onValueChanged={(e) => setEmailFromName(e.value)}
                    placeholder='Bedrijfsnaam'
                    stylingMode='outlined'
                  />
                </div>
                <div className='form-row hint-row'>
                  <span className='hint-text'>
                    Als deze velden leeg zijn, wordt de standaard afzender gebruikt.
                  </span>
                </div>
              </div>
              <div className='card-footer'>
                <Button
                  text='Opslaan'
                  icon='save'
                  type='default'
                  stylingMode='contained'
                  disabled={isSaving}
                  onClick={saveCompanyInfo}
                />
              </div>
            </div>

            {/* ── Boekhouding Integratie ── */}
            <div className='dx-card info-card'>
              <div className='card-header'>
                <i className='dx-icon dx-icon-money card-header-icon' />
                <span>Boekhouding Integratie</span>
                {hasExactConnection && (
                  <span className='connection-badge connected'>Verbonden</span>
                )}
                {providerType === 'ExactOnline' && !hasExactConnection && (
                  <span className='connection-badge not-connected'>Niet verbonden</span>
                )}
              </div>
              <div className='card-body-form'>
                <div className='form-row'>
                  <label className='form-label'>Provider</label>
                  <SelectBox
                    items={providerOptions}
                    displayExpr='text'
                    valueExpr='value'
                    value={providerType}
                    onValueChanged={(e) => setProviderType(e.value)}
                    stylingMode='outlined'
                  />
                </div>

                {providerType === 'ExactOnline' && (
                  <>
                    <div className='form-row'>
                      <label className='form-label'>Client ID</label>
                      <TextBox
                        value={exactClientId}
                        onValueChanged={(e) => setExactClientId(e.value)}
                        placeholder='Exact Online Client ID'
                        stylingMode='outlined'
                      />
                    </div>
                    <div className='form-row'>
                      <label className='form-label'>Client Secret</label>
                      <TextBox
                        value={exactClientSecret}
                        onValueChanged={(e) => setExactClientSecret(e.value)}
                        placeholder={hasExactSecret ? '••••••••••••  (opgeslagen, laat leeg om te behouden)' : 'Exact Online Client Secret'}
                        mode='password'
                        stylingMode='outlined'
                      />
                    </div>
                    <div className='form-row'>
                      <label className='form-label'>Callback URL</label>
                      <TextBox
                        value={exactCallbackUrl}
                        onValueChanged={(e) => setExactCallbackUrl(e.value)}
                        placeholder='Wordt automatisch gegenereerd'
                        stylingMode='outlined'
                      />
                    </div>
                    <div className='form-row hint-row'>
                      <span className='hint-text'>
                        De Callback URL moet exact overeenkomen met de Redirect URI in het Exact Online App Center.
                        Laat leeg voor automatische generatie.
                      </span>
                    </div>
                  </>
                )}
              </div>
              <div className='card-footer'>
                <Button
                  text='Opslaan'
                  icon='save'
                  type='default'
                  stylingMode='contained'
                  disabled={isSaving}
                  onClick={saveAccountingProvider}
                />
                {providerType === 'ExactOnline' && !hasExactConnection && (
                  <Button
                    text='Verbinden met Exact Online'
                    icon='key'
                    type='success'
                    stylingMode='contained'
                    disabled={isSaving || !exactClientId}
                    onClick={handleConnectExact}
                  />
                )}
                {providerType === 'ExactOnline' && hasExactConnection && (
                  <Button
                    text='Opnieuw verbinden'
                    icon='refresh'
                    type='normal'
                    stylingMode='outlined'
                    disabled={isSaving}
                    onClick={handleConnectExact}
                  />
                )}
                {providerType !== 'None' && (
                  <Button
                    text='Koppeling verwijderen'
                    icon='trash'
                    type='danger'
                    stylingMode='outlined'
                    disabled={isSaving}
                    onClick={handleRemoveProvider}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </ScrollView>
  );
};
